import { styled } from "styled-components";
import { useEffect, useRef, useState } from "react";
import { Divider, Empty, Menu, Select } from "antd";
import { SupportCardList } from "../../components";
import {
  STARTUP_SUPPORT_MENUS_EN,
  STARTUP_SUPPORT_MENUS_JA,
} from "../../utils/startupSupportMenus";
import {
  STARTUP_STATUS_EN,
  STARTUP_STATUS_JP,
} from "../../utils/dummyStatusSupport";
import { theme } from "shared/src/theme";
import { i18n } from "shared";
import { debounce } from "../../utils/debounce";
import { useTranslation } from "next-i18next";
import Head from "next/head";
import DownIcon from "../../assets/downIcon.svg";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";

const Wrapper = styled.div`
  margin-top: 94px;
  margin-bottom: 65px;
  max-width: 1440px;
  display: flex;
  margin-left: auto;
  margin-right: auto;

  & .ant-collapse {
    background-color: ${theme.base};
    border: none;
  }
  @media (max-width: 1550px) {
    padding: 0px 5%;
  }
  @media (max-width: 1440px) {
    padding: 0px 9.02% 0px 8.33%;
  }
  @media (max-width: 1245px) {
    padding: 0px 5%;
  }
  @media (max-width: 1150px) {
    padding: 0px 3%;
  }

  @media (max-width: 1095px) {
    margin-top: 82px;
  }

  @media (max-width: 915px) {
    & .ant-divider {
      display: none;
    }
  }

  @media (min-width: 916px) {
    & .ant-collapse {
      display: none;
    }
  }
  @media (max-width: 768px) {
    padding: 0px 16px;
  }

  @media (max-width: 625px) {
    margin-bottom: 33px;
  }
`;

const Content = styled.div`
  width: 100%;
  position: relative;
  & .ant-divider {
    border-color: ${theme.gray3};
    margin: 40px 0;
  }

  & .ant-collapse {
    margin-bottom: 32px;
  }
  & .ant-collapse-extra {
    margin-right: 10px;

    & svg {
      width: 24px;
      height: 24px;
    }

    & path {
      fill: ${theme.grey2};
    }
  }

  & .panel-title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  & .ant-collapse-content-box {
    padding: 34px 0px 0px !important;
  }

  & .ant-collapse-header {
    font-size: 16px;
    line-height: 21px;
    justify-content: flex-end;
    display: flex;
    flex-direction: row-reverse;
    font-weight: 400;
    color: ${theme.grey2} !important;
    padding: 0px 40px 0px 0px !important;
  }

  & .ant-collapse-item-active .ant-collapse-header {
    color: ${theme.gray0} !important;
    font-weight: 600;

    & .ant-collapse-extra > svg {
      & path,
      circle {
        fill: ${theme.gray0};
      }
    }
  }
`;

const Section = styled.div`
  & h1 {
    ${theme.typography.headline2}
    margin-bottom: 32px;
    display: flex;
    & svg {
      width: 32px;
      height: 32px;
      margin-right: 16px;
    }
    & path,
    & circle {
      fill: ${theme.gray0};
    }
  }
  @media (max-width: 915px) {
    margin-top: 20px;
  }
`;

const Sidebar = styled.div`
  width: 40%;
  margin-right: 20px;

  & .ant-menu-title-content {
    white-space: normal;
  }
  & ul {
    position: sticky;
    top: 195px;
    max-height: calc(100vh - 185px);
    overflow-y: scroll;
    overflow-x: hidden;

    @media (max-width: 1095px) {
      top: 162px;
    }
  }
  & .ant-menu-item {
    height: auto;
    padding: 5px 0;
    min-height: 40px;
  }

  @media (max-width: 1125px) {
    width: 45%;
  }

  @media (max-width: 915px) {
    display: none;
  }
`;

const SelectContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: sticky;
  top: 92px;
  z-index: 1;
  background: white;
  padding: 15px 0px;

  & .ant-select-arrow {
    @media (max-width: 480px) {
      margin-top: 5px;
    }
  }
  & .ant-select-selector {
    & span {
      font-size: 16px;
    }
    min-width: 135px;
    min-height: 48px;
    padding: 0 12px !important;

    border-color: ${theme.buttonRed} !important;
    border-radius: 8px !important;

    @media (max-width: 365px) {
      max-width: 107px;
    }
    .ant-select-selection-placeholder {
      padding-top: 8px;
      font-weight: 700;
      color: ${theme.buttonRed};
    }
    @media (max-width: 480px) {
      margin-top: 20px;
    }
  }

  & .ant-select-selection-item {
    margin: auto;
    font-weight: 600;
    color: ${theme.buttonRed};
    font-size: 16px;
  }

  @media (max-width: 490px) {
    margin-top: 50px;
  }
  @media (max-width: 360px) {
    margin-top: 0px;
  }
`;

const Title = styled.div`
  position: absolute;
  height: 32px;
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  margin-top: 25px;
  z-index: 100;

  @media (max-width: 670px) {
    height: 24px;
    left: 16px;
    margin-top: 44px;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.0015em;
  }
  @media (max-width: 360px) {
    margin-top: 0px;
  }
`;

const StartupSupport = () => {
  const { t } = useTranslation("common");
  const [selectedMenu, setSelectedMenu] = useState("menu0");
  const [menu, setMenu] = useState(null);
  const [contents, setContents] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const PREFECTURE_OPTIONS = [
    { label: t("Osaka (Osaka Prefecture and Osaka City)"), value: "osaka" },
    { label: t("Sakai"), value: "sakai" },
    {
      label: t("HyogoKobe（Hyogo Prefecture and Kobe City）"),
      value: "hyogo_kobe",
    },
    { label: t("Kyoto (Kyoto Prefecture and Kyoto City)"), value: "kyoto" },
    { label: t("Shiga"), value: "shiga" },
    { label: t("Wakayama"), value: "wakayama" },
    { label: t("Tokushima"), value: "tokushima" },
    { label: t("Tottori"), value: "tottori" },
  ];

  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);
  const ref4 = useRef(null);
  const ref5 = useRef(null);
  const ref6 = useRef(null);
  const ref7 = useRef(null);
  const ref8 = useRef(null);
  const ref9 = useRef(null);
  const ref10 = useRef(null);
  const ref11 = useRef(null);
  const ref12 = useRef(null);
  const ref13 = useRef(null);
  const ref14 = useRef(null);
  const ref15 = useRef(null);
  const ref16 = useRef(null);
  const ref17 = useRef(null);
  const ref18 = useRef(null);
  const ref19 = useRef(null);
  const ref20 = useRef(null);
  const ref21 = useRef(null);

  const sectionRefs = [
    { ref: ref1, section: "menu0" },
    { ref: ref2, section: "menu1" },
    { ref: ref3, section: "menu2" },
    { ref: ref4, section: "menu3" },
    { ref: ref5, section: "menu4" },
    { ref: ref6, section: "menu5" },
    { ref: ref7, section: "menu6" },
    { ref: ref8, section: "menu7" },
    { ref: ref9, section: "menu8" },
    { ref: ref10, section: "menu9" },
    { ref: ref11, section: "menu10" },
    { ref: ref12, section: "menu11" },
    { ref: ref13, section: "menu12" },
    { ref: ref14, section: "menu13" },
    { ref: ref15, section: "menu14" },
    { ref: ref16, section: "menu15" },
    { ref: ref17, section: "menu16" },
    { ref: ref18, section: "menu17" },
    { ref: ref19, section: "menu18" },
    { ref: ref20, section: "menu19" },
    { ref: ref21, section: "menu20" },
  ];

  const scrollToId = (id) => {
    const view = process.browser && document.getElementById(id);
    if (view) {
      const yOffset = -200;
      const y = view.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const debounceFunction = debounce((handleFunction: any) => {
    handleFunction();
    return null;
  }, 200);

  const getDimensions = (ele) => {
    const { height } = ele.getBoundingClientRect();

    const offsetTop = ele.offsetTop;
    const offsetBottom = offsetTop + height;

    return {
      height,
      offsetTop,
      offsetBottom,
    };
  };

  const handleScroll = () => {
    const negativeOffsetY = 440;
    const scrollPosition = window.scrollY + negativeOffsetY;
    if (scrollPosition <= 440) {
      setSelectedMenu("menu0");
      return;
    }

    const selected = sectionRefs.find(({ ref }) => {
      const ele = ref.current;
      if (ele) {
        const { offsetBottom, offsetTop } = getDimensions(ele);

        return scrollPosition > offsetTop && scrollPosition < offsetBottom;
      }
    });

    if (selected && selected.section !== selectedMenu) {
      setSelectedMenu(selected.section);
    }
  };

  const debouncedScroll = () => {
    debounceFunction(handleScroll);
  };

  useEffect(() => {
    const key = localStorage.getItem("support-key");
    const cityKey = localStorage.getItem("support-key-city");
    if (cityKey) {
      setSelectedCity(cityKey);
    }
    if (key) {
      setTimeout(() => {
        scrollToId(key);
        localStorage.removeItem("support-key");
        localStorage.removeItem("support-key-city");
      }, [500]);
    }
  }, []);

  useEffect(() => {
    debounceFunction(handleScroll);
    window.addEventListener("scroll", debouncedScroll);
    return () => {
      window.removeEventListener("scroll", debouncedScroll);
    };
  }, [selectedMenu]);

  const getFilterData = (allMenus, allContents) => {
    const citiesContents = {};
    const citiesMenu = [];
    let count = 0;
    allMenus.map((menuItem, index) => {
      const filteredContents = allContents[index].filter(
        (content) => content?.city === selectedCity
      );
      if (filteredContents.length !== 0) {
        citiesMenu.push(menuItem);
        citiesContents[count] = filteredContents;
        count += 1;
      }
    });
    setMenu(citiesMenu);
    setContents(citiesContents);
  };

  useEffect(() => {
    if (i18n.language.startsWith("ja")) {
      if (selectedCity) {
        getFilterData(STARTUP_SUPPORT_MENUS_JA, STARTUP_STATUS_JP);
      } else {
        setMenu(STARTUP_SUPPORT_MENUS_JA);
        setContents(STARTUP_STATUS_JP);
      }
    } else {
      if (selectedCity) {
        getFilterData(STARTUP_SUPPORT_MENUS_EN, STARTUP_STATUS_EN);
      } else {
        setMenu(STARTUP_SUPPORT_MENUS_EN);
        setContents(STARTUP_STATUS_EN);
      }
    }
  }, [i18n.language, selectedCity]);

  const handleSelectClick = (value) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setSelectedCity(value);
  };

  return (
    <Wrapper>
      <Head>
        <title>
          {t(
            "kansai startup support,kansai startup grants,kansai startup subsidies"
          )}
        </title>
        <meta
          name="keywords"
          content={t(
            "Kansai Startup Support,Kansai Startup Grants,Kansai Startup Subsidies,Kansai Startup Incentives,Kansai Startup Seminars,Kansai Startup Mentoring,Kansai Startup Loans,Kansai Startup Matching"
          )}
        />
        <meta
          key="startup_support"
          name="description"
          content={t(
            "This is the list page of Kansai Startup Support. Not only incubation and acceleration, but also seminars, workshops, mentoring, subsidies, grants, incentives, funds, loans/capital loans, matching, and more."
          )}
        />
        <meta
          name="og:description"
          content={t(
            "This is the list page of Kansai Startup Support. Not only incubation and acceleration, but also seminars, workshops, mentoring, subsidies, grants, incentives, funds, loans/capital loans, matching, and more."
          )}
        />
      </Head>
      <Title>
        <>{t("Kansai Startup Supports")}</>
      </Title>
      {menu && menu.length !== 0 && (
        <Sidebar>
          <Menu
            mode="inline"
            className="side-menu"
            selectedKeys={[selectedMenu]}
          >
            {menu.map((menu, index) => (
              <Menu.Item
                key={`menu${index}`}
                onClick={() => scrollToId(`menu${index}`)}
              >
                {menu.icon} {menu.label}
              </Menu.Item>
            ))}
          </Menu>
        </Sidebar>
      )}

      <Content>
        <SelectContainer>
          <Select
            options={PREFECTURE_OPTIONS}
            placeholder={t("Search by city")}
            allowClear={!!selectedCity}
            value={selectedCity}
            suffixIcon={<DownIcon />}
            onChange={handleSelectClick}
          />
        </SelectContainer>
        {menu && menu.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t("No data available for display.")}
          />
        )}
        {menu &&
          menu.map((item, index) => (
            <Section ref={sectionRefs[index].ref} key={index}>
              <SupportCardList
                icon={item.icon}
                title={item.label}
                id={`menu${index}`}
                lists={contents[index]}
              />
              {index !== menu.length - 1 && <Divider />}
            </Section>
          ))}
      </Content>
    </Wrapper>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
    },
  };
}

export default StartupSupport;
